/**
 * ModalFocusTrap — one global Tab trap for the app's modals.
 *
 * Mounted once in OutlineView. When a modal overlay is on screen, Tab / Shift+Tab cycle only through
 * the focusables INSIDE the topmost modal — so focus can't fall back to the outline, the left nav, or
 * any background menu (the reported close-out-phase bug). Modals are matched by role / aria-modal, a
 * `data-modal` opt-in, or the app's known overlay classes. The Time Review modal (.tr-overlay) runs
 * its own trap, so we defer to it.
 *
 * Capture phase so we win before anything else; we only act on Tab, leaving every other key alone.
 */

import { useEffect } from 'react'

// Topmost-wins: later matches in the DOM are more likely to be the active modal. role/aria/data-modal
// are the general signals; the class names cover existing modals that predate them.
const MODAL_SELECTOR = '[role="dialog"], [aria-modal="true"], [data-modal], .wf-closeout-overlay, .aj-proj-info-overlay'

function topmostModal() {
  // .tr-overlay owns its own trap — bow out entirely if it's up.
  if (document.querySelector('.tr-overlay')) return null
  const nodes = Array.from(document.querySelectorAll(MODAL_SELECTOR)).filter(el => el.offsetParent !== null || el.getClientRects().length)
  return nodes.length ? nodes[nodes.length - 1] : null
}

function focusables(root) {
  const sel = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  return Array.from(root.querySelectorAll(sel)).filter(el => el.offsetParent !== null && !el.disabled && el.getAttribute('aria-hidden') !== 'true')
}

export default function ModalFocusTrap() {
  useEffect(() => {
    function onKeyDown(e) {
      if (e.key !== 'Tab') return
      const modal = topmostModal()
      if (!modal) return
      const list = focusables(modal)
      if (!list.length) { e.preventDefault(); return }   // nothing to focus, but don't let Tab escape
      const first = list[0], last = list[list.length - 1], active = document.activeElement
      const inside = modal.contains(active)
      if (e.shiftKey) {
        if (!inside || active === first) { e.preventDefault(); last.focus() }
      } else {
        if (!inside || active === last) { e.preventDefault(); first.focus() }
      }
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [])
  return null
}
